/**
 * TubeGuide — a structured, checkable rendering of the LabAide draw guide.
 *
 * Replaces the old flat "• line\n• line\n…" dump with one panel per collection
 * step (each tube, plus any urine/stool/swab specimen), in draw order, with:
 *   - the tests that go in that tube (as chips),
 *   - the handling instructions for it,
 *   - a checkoff the phleb ticks once that tube is drawn/prepared,
 *   - a progress bar so the field + admin can see how far along the draw is.
 *
 * Presentational only. The parent owns the LabAide result, the saved progress
 * map, and the toggle handler — so the SAME component renders the interactive
 * phleb view and the read-only admin view.
 */
import React, { useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Flex,
  Badge,
  Spinner,
  Icon,
  Divider,
  Progress,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import { FaCheck, FaCheckCircle, FaExclamationTriangle, FaVial } from 'react-icons/fa';
import { parseTubeGuide } from '../../utils/labaideTube';

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

// Tint each step panel by what's being collected — purely cosmetic, helps the
// phleb tell the blood tubes apart from the urine/stool cup at a glance.
const collectTint = {
  blood: { bg: 'red.50', border: 'red.100' },
  urine: { bg: 'yellow.50', border: 'yellow.200' },
  stool: { bg: 'orange.50', border: 'orange.100' },
  swab: { bg: 'blue.50', border: 'blue.100' },
};

function StepPanel({ step, index, checked, checkedMeta, onToggle, readOnly, saving }) {
  const tint = collectTint[step.collect] || collectTint.blood;
  const interactive = !readOnly && !!onToggle;
  const toggle = () => {
    if (interactive && !saving) onToggle(step.key, !checked);
  };

  return (
    <Flex
      align="stretch"
      bg={checked ? 'green.50' : 'white'}
      border="2px solid"
      borderColor={checked ? 'green.400' : tint.border}
      borderRadius="lg"
      overflow="hidden"
      transition="background-color 0.15s, border-color 0.15s"
      cursor={interactive ? 'pointer' : 'default'}
      onClick={toggle}
      _hover={interactive ? { borderColor: checked ? 'green.500' : 'gray.300' } : undefined}
    >
      {/* Big tap-target checkbox on the left — fills green once collected, with
          a TAP / DONE label so it's obvious the phleb marks it off here. */}
      <Flex
        flexShrink={0}
        w={{ base: 16, sm: 20 }}
        direction="column"
        align="center"
        justify="center"
        gap={1}
        bg={checked ? 'green.500' : tint.bg}
        borderRight="2px solid"
        borderColor={checked ? 'green.400' : tint.border}
      >
        {saving ? (
          <Spinner size="md" color={checked ? 'white' : 'green.500'} />
        ) : checked ? (
          <Icon as={FaCheck} color="white" boxSize={7} />
        ) : (
          <Box w={9} h={9} borderRadius="md" border="2px solid" borderColor={interactive ? 'green.500' : 'gray.300'} bg="white" />
        )}
        {(interactive || checked) && (
          <Text
            fontSize="2xs"
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.05em"
            color={checked ? 'white' : 'green.600'}
          >
            {checked ? 'Done' : 'Tap'}
          </Text>
        )}
      </Flex>

      {/* Tube info */}
      <Box flex={1} minW={0} p={3}>
        <HStack spacing={1.5} flexWrap="wrap">
          <Flex
            w={5}
            h={5}
            flexShrink={0}
            borderRadius="full"
            bg={checked ? 'green.500' : 'gray.200'}
            color={checked ? 'white' : 'gray.600'}
            align="center"
            justify="center"
            fontWeight="bold"
            fontSize="2xs"
          >
            {index + 1}
          </Flex>
          <Text fontSize="sm" fontWeight="bold" color="gray.800">
            {step.emoji} {step.label}
          </Text>
          <Badge colorScheme={checked ? 'green' : 'gray'} fontSize="2xs" borderRadius="full">
            {step.tests.length} {step.tests.length === 1 ? 'test' : 'tests'}
          </Badge>
        </HStack>

        {step.tests.length > 0 && (
          <Flex gap={1.5} flexWrap="wrap" mt={2}>
            {step.tests.map((t, i) => (
              <Badge
                key={`${t.code || t.name}-${i}`}
                colorScheme="purple"
                variant="subtle"
                fontSize="2xs"
                borderRadius="full"
                px={2}
              >
                {t.name}
                {t.code ? ` (${t.code})` : ''}
              </Badge>
            ))}
          </Flex>
        )}

        {step.handling.length > 0 && (
          <VStack align="stretch" spacing={0.5} mt={2}>
            {step.handling.map((h, i) => (
              <Text key={i} fontSize="xs" color="gray.600">
                {h}
              </Text>
            ))}
          </VStack>
        )}

        {checked && checkedMeta?.at && (
          <Text fontSize="2xs" color="green.600" fontWeight="600" mt={2}>
            Collected · {formatWhen(checkedMeta.at)}
            {checkedMeta.by ? ` · ${checkedMeta.by}` : ''}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

export default function TubeGuide({
  result,
  progress = {},
  onToggleStep,
  readOnly = false,
  savingKey = null,
}) {
  const status = result?.status;
  const guide = useMemo(
    () => (status === 'done' ? parseTubeGuide(result.bullets) : null),
    [status, result]
  );
  const { isOpen: warningsOpen, onToggle: toggleWarnings } = useDisclosure();

  const steps = guide?.steps || [];
  const doneCount = steps.filter((s) => progress?.[s.key]?.at).length;
  const allDone = steps.length > 0 && doneCount === steps.length;

  return (
    <Box bg="purple.50" borderRadius="lg" border="1px solid" borderColor="purple.100" p={4}>
      <HStack spacing={2} mb={3} justify="space-between">
        <HStack spacing={2}>
          <Icon as={FaVial} boxSize={4} color="purple.500" />
          <Text fontSize="sm" fontWeight="bold" color="purple.700">
            Test Tube Guide
          </Text>
        </HStack>
        {guide?.summary && (
          <Text fontSize="xs" color="purple.600" fontWeight="medium" textAlign="right" noOfLines={1}>
            {guide.summary}
          </Text>
        )}
      </HStack>

      {/* Loading / non-success states. */}
      {(!result || status === 'loading') && (
        <HStack spacing={2} color="gray.400">
          <Spinner size="xs" />
          <Text fontSize="xs">Loading collection guide…</Text>
        </HStack>
      )}
      {status === 'ambiguous' && (
        <Text fontSize="xs" color="orange.600">
          Collection site needed — confirm against the order.
        </Text>
      )}
      {status === 'error' && (
        <Text fontSize="xs" color="gray.400">
          Tube guide unavailable — check the script.
        </Text>
      )}
      {status === 'done' && steps.length === 0 && (
        <Text fontSize="xs" color="gray.400">
          No tube guidance returned — check the script.
        </Text>
      )}

      {status === 'done' && steps.length > 0 && (
        <>
          {/* Progress — how many of the collection steps are done. */}
          <Box mb={3}>
            <Flex justify="space-between" align="center" mb={1}>
              <Text fontSize="xs" fontWeight="600" color="gray.600">
                {allDone ? 'All collected' : `${doneCount} of ${steps.length} collected`}
              </Text>
              {allDone && (
                <HStack spacing={1} color="green.600">
                  <Icon as={FaCheckCircle} boxSize={3.5} />
                  <Text fontSize="xs" fontWeight="600">
                    Ready for the lab
                  </Text>
                </HStack>
              )}
            </Flex>
            <Progress
              value={steps.length ? (doneCount / steps.length) * 100 : 0}
              size="sm"
              colorScheme="green"
              borderRadius="full"
              bg="white"
            />
            {onToggleStep && !readOnly && !allDone && (
              <Text fontSize="2xs" color="gray.500" mt={1}>
                👈 Tap each tube on the left as you collect it.
              </Text>
            )}
          </Box>

          {/* One panel per draw-order step. */}
          <VStack align="stretch" spacing={2}>
            {steps.map((step, i) => (
              <StepPanel
                key={step.key}
                step={step}
                index={i}
                checked={!!progress?.[step.key]?.at}
                checkedMeta={progress?.[step.key]}
                onToggle={onToggleStep}
                readOnly={readOnly || !onToggleStep}
                saving={savingKey === step.key}
              />
            ))}
          </VStack>

          {/* Warnings — collapsed by default so they don't dominate the panel. */}
          {guide.warnings.length > 0 && (
            <Box mt={3}>
              <HStack
                as="button"
                spacing={2}
                onClick={toggleWarnings}
                color="orange.600"
                _hover={{ color: 'orange.700' }}
              >
                <Icon as={FaExclamationTriangle} boxSize={3} />
                <Text fontSize="xs" fontWeight="600">
                  {guide.warnings.length} {guide.warnings.length === 1 ? 'note' : 'notes'} —{' '}
                  {warningsOpen ? 'hide' : 'show'}
                </Text>
              </HStack>
              <Collapse in={warningsOpen} animateOpacity>
                <VStack align="stretch" spacing={1} mt={2}>
                  {guide.warnings.map((w, i) => (
                    <Text key={i} fontSize="xs" color="orange.700">
                      ⚠ {w}
                    </Text>
                  ))}
                </VStack>
              </Collapse>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
